/**
 * Accessible descriptions for the reference photographs.
 *
 * TEMPORARY — these belong in the translations sheet, which has no rows for
 * them. They live here for the same reason the outcome button labels are
 * placeholders: the page must not ship describing safety-critical images in
 * English to a French or Italian reader.
 *
 * They are deliberately functional rather than editorial: each one states what
 * the photograph shows, because for a screen-reader user the alt text is the
 * only channel carrying the OK / NOK distinction that sighted readers get from
 * the labels burnt into the images.
 *
 * When the sheet gains rows, delete this file and read the keys through
 * `Translate` like everything else.
 */

/** Identifiers for the six reference photographs. */
export type AltKey =
  | 'rivetOk'
  | 'rivetNok'
  | 'push1Ok'
  | 'push1Nok'
  | 'push2Ok'
  | 'push2Nok'
  | 'video'

type AltStrings = Record<AltKey, string>

const en: AltStrings = {
  rivetOk: 'Compliant rivet: the end is rounded, shaped like a mushroom',
  rivetNok: 'Defective rivet: the end is flat',
  push1Ok: 'First check, sound carabiner: the wire gate stays in its housing',
  push1Nok: 'First check, defective carabiner: the wire gate comes out of its housing',
  push2Ok: 'Second check, sound carabiner: the wire gate stays in its housing',
  push2Nok:
    'Second check, defective carabiner: the wire gate comes out of its housing',
  video: 'How to check your quickdraw',
}

const fr: AltStrings = {
  rivetOk: 'Rivetage conforme : extrémité arrondie, en forme de champignon',
  rivetNok: 'Rivetage défectueux : extrémité plate',
  push1Ok: 'Premier contrôle, mousqueton conforme : le doigt fil reste dans son logement',
  push1Nok:
    'Premier contrôle, mousqueton défectueux : le doigt fil sort de son logement',
  push2Ok:
    'Second contrôle, mousqueton conforme : le doigt fil reste dans son logement',
  push2Nok:
    'Second contrôle, mousqueton défectueux : le doigt fil sort de son logement',
  video: 'Comment vérifier votre dégaine',
}

const it: AltStrings = {
  rivetOk: 'Rivettatura conforme: estremità arrotondata, a forma di fungo',
  rivetNok: 'Rivettatura difettosa: estremità piatta',
  push1Ok: 'Primo controllo, moschettone conforme: il filo resta nella sua sede',
  push1Nok: 'Primo controllo, moschettone difettoso: il filo esce dalla sua sede',
  push2Ok: 'Secondo controllo, moschettone conforme: il filo resta nella sua sede',
  push2Nok: 'Secondo controllo, moschettone difettoso: il filo esce dalla sua sede',
  video: 'Come verificare il tuo rinvio',
}

const BY_LOCALE: Record<string, AltStrings> = { en, fr, it }

/**
 * Describe a reference photograph in the reader's language.
 *
 * Falls back to English for locales still awaiting country validation, which is
 * what the rest of the page does for them too.
 *
 * @param key - Which photograph to describe
 * @param locale - Active locale, e.g. `fr`
 * @returns The description
 */
export const altText = (key: AltKey, locale: string | undefined): string =>
  (BY_LOCALE[locale ?? 'en'] ?? en)[key]
